import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useState } from "react";
import { TABS } from "./config";
import { useFaixaDeAbas } from "./faixa-abas";
import type { Row, AlertasData } from "./types";
import { api, setSessionUser } from "./api";
import { ICONS, IconDashboard, IconSun, IconMoon, IconLogout, IconHome } from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import { AvisoReuniao } from "./components/PedirReuniaoView";
import Login from "./components/Login";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import { SYSTEMS, goToSystem, readSsoUser, clearSsoFromUrl } from "./integration";
import Dashboard from "./components/Dashboard";
import CrudView from "./components/CrudView";
import AlertasView from "./components/AlertasView";
import DocumentosView from "./components/DocumentosView";
import SolicitacoesView from "./components/SolicitacoesView";
import RecebimentosView from "./components/RecebimentosView";
import AlertasBanner from "./components/AlertasBanner";
import AvisosBanner from "./components/AvisosBanner";
import { AccessChecking, AccessDenied } from "./components/AccessGate";
import { PermissoesProvider } from "./permissoes";
import {
  ACESSO_COMPLETO,
  ativarModoLeitura,
  podeVerAba,
  primeiraAbaLiberada,
  visualizacaoDaResposta,
  type Visualizacao,
} from "./modo-leitura";

const SESSION_KEY = "sercamp-almox-user";

/** Login global do Portal — sempre liberado e sempre admin (sem cadastro local). */
const ADMIN_SUPREMO_LOGIN = "admin@portalsercamp.com";
const isAdminSupremo = (u: string | null) =>
  (u ?? "").trim().toLowerCase() === ADMIN_SUPREMO_LOGIN;

/** Estado do portão de acesso ao sistema (consulta o Portal). */
type AccessState = "checking" | "allowed" | "denied" | "neterror";

/** Login inicial: prioriza o SSO vindo do Portal/outros sistemas (URL) e cai na sessão salva. */
function getInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Aba inicial via deep-link do Portal (#tab=chave); cai em "dashboard" se inválida. */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && TABS.some((t) => t.key === k) ? k : "dashboard";
  } catch {
    return "dashboard";
  }
})();

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(getInitialUser);

  // Limpa o #sso= da URL após resolver o usuário inicial (barra de endereço limpa).
  useEffect(() => {
    clearSsoFromUrl();
  }, []);

  const [active, setActive] = useState<string>(INITIAL_TAB);
  const [itens, setItens] = useState<Row[]>([]);
  const [alertas, setAlertas] = useState<AlertasData | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Só administradores (cargo/checkbox no Portal) podem EXCLUIR registros.
  const [podeExcluir, setPodeExcluir] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessState>("checking");
  /* No celular o menu vira uma faixa que rola de lado: isto mantem a aba
     aberta a vista (ver src/faixa-abas.tsx). */
  const navRef = useFaixaDeAbas(active, access === "allowed");
  // Somente visualização (cadastro do Portal): abas liberadas e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);

  // PORTÃO DE ACESSO: sempre que há usuário atual (login novo ou sessão
  // restaurada), confirma no Portal se ele pode usar este sistema antes de
  // liberar o app. Falha de rede => não libera (tela clara, sem loop).
  useEffect(() => {
    if (!user) {
      setAccess("checking");
      return;
    }
    let cancelled = false;
    setAccess("checking");
    api
      .acesso(user)
      .then((r) => {
        if (cancelled) return;
        setVis(visualizacaoDaResposta(r));
        const allowed = !!r.allowed || !!r.adminSupremo || isAdminSupremo(user);
        if (allowed) {
          setAccess("allowed");
        } else if (r.code === "PORTAL_INDISPONIVEL") {
          // Não é "não pode": é "não deu para saber". A sessão fica salva —
          // apagá-la faria uma piscada do Portal deslogar quem está trabalhando.
          setAccess("neterror");
        } else {
          // Bloqueado: anula a sessão para não restaurar o app ao recarregar.
          setAccess("denied");
          localStorage.removeItem(SESSION_KEY);
          encerrarSessao();
        }
      })
      .catch(() => {
        if (!cancelled) setAccess("neterror");
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Resolve se o usuário logado é administrador (define quem vê/gerencia "Usuários").
  useEffect(() => {
    setSessionUser(user ?? "");
    if (!user) {
      setIsAdmin(false);
      setPodeExcluir(false);
      setDisplayName(null);
      return;
    }
    api
      .me(user)
      .then((r) => {
        setIsAdmin(!!r.isAdmin);
        setPodeExcluir(!!r.podeExcluir);
        setDisplayName(r.nome || null);
      })
      .catch(() => {
        setIsAdmin(false);
        setPodeExcluir(false);
        setDisplayName(null);
      });
  }, [user]);

  // Não-admin não acessa a aba de usuários (nem por deep-link).
  useEffect(() => {
    if (active === "usuarios" && !isAdmin) setActive("dashboard");
  }, [active, isAdmin]);

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(access === "allowed" && vis.somenteLeitura);
  }, [access, vis.somenteLeitura]);

  // Aba fora das liberadas (inclusive por deep-link) cai na primeira liberada.
  const verAba = (k: string) =>
    podeVerAba(vis, k) && !(vis.somenteLeitura && (k === "usuarios" || k === "solicitar_atualizacao"));
  useEffect(() => {
    if (!verAba(active)) {
      const primeira = primeiraAbaLiberada(vis, ["dashboard", ...TABS.map((t) => t.key)].filter(verAba));
      if (primeira) setActive(primeira);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, vis]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  const loadItens = useCallback(async () => {
    try {
      setItens(await api.list("itens"));
    } catch {
      /* mostrado nas telas individuais */
    }
  }, []);

  const loadAlertas = useCallback(async () => {
    try {
      setAlertas(await api.alertas.get());
    } catch {
      /* alerta é secundário — falha silenciosa */
    }
  }, []);

  useEffect(() => {
    if (user && access === "allowed") {
      loadItens();
      loadAlertas();
    }
  }, [user, access, loadItens, loadAlertas]);

  const onDataChanged = useCallback(() => {
    loadItens();
    loadAlertas();
    setRefreshKey((k) => k + 1);
  }, [loadItens, loadAlertas]);

  function login(name: string) {
    localStorage.setItem(SESSION_KEY, name);
    setUser(name);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setUser(null);
    setVis(ACESSO_COMPLETO);
  }

  if (!user) {
    return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;
  }

  // Portão de acesso: enquanto verifica/bloqueia, não renderiza o app.
  if (access === "checking") {
    return (
      <div className="app">
        <AnimatedBackground />
        <main className="content">
          <AccessChecking />
        </main>
      </div>
    );
  }
  if (access === "denied" || access === "neterror") {
    return (
      <div className="app">
        <AnimatedBackground />
        <main className="content">
          <AccessDenied user={user} networkError={access === "neterror"} />
        </main>
      </div>
    );
  }

  const tab = TABS.find((t) => t.key === active);
  const title = active === "dashboard" ? "Dashboard" : tab?.label ?? "";
  const subtitle =
    active === "dashboard"
      ? "Visão geral do Almoxarifado"
      : tab?.subtitle ?? "";

  const conteudo = (
    <div className="app">
      <AnimatedBackground />
      <AvisoReuniao usuario={user} onAbrir={() => setActive("solicitar_atualizacao")} />

      <aside className="sidebar">
        <div className="brand">
          <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
        </div>

        <nav className="nav" ref={navRef}>
          {verAba("dashboard") && (
            <button
              className={`nav-item ${active === "dashboard" ? "active" : ""}`}
              onClick={() => setActive("dashboard")}
            >
              <span className="nav-icon">
                <IconDashboard size={19} />
              </span>
              Dashboard
            </button>
          )}
          {TABS.filter((t) => (t.key !== "usuarios" || isAdmin) && verAba(t.key)).map((t) => {
            const Icon = ICONS[t.icon];
            return (
              <button
                key={t.key}
                className={`nav-item ${active === t.key ? "active" : ""}`}
                onClick={() => setActive(t.key)}
              >
                <span className="nav-icon">
                  <Icon size={19} />
                </span>
                {t.label}
              </button>
            );
          })}
        </nav>

      </aside>

      <main className="content">
        <header className="topbar">
          <div>
            <h1>{title}</h1>
            <p className="muted">{subtitle}</p>
          </div>

          <div className="topbar-actions">
            <button
              className="theme-toggle"
              onClick={() => goToSystem(SYSTEMS.portal, user)}
              title="Portal SERCAMP"
            >
              <IconHome size={18} />
            </button>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={theme === "dark" ? "Tema claro" : "Tema escuro"}
            >
              {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
            </button>
            <div className="user-chip">
              <span className="user-avatar">
                {user.charAt(0).toUpperCase()}
              </span>
              <span className="user-name">{displayName || user}</span>
            </div>
            <button className="theme-toggle" onClick={logout} title="Sair">
              <IconLogout size={18} />
            </button>
          </div>
        </header>

        <AvisosBanner />

        {/* A faixa só leva para a aba de Alertas: quem não vê a aba não vê a faixa. */}
        {active !== "alertas" && verAba("alertas") && (
          <AlertasBanner data={alertas} onVer={() => setActive("alertas")} />
        )}

        <section className="view">
          {!verAba(active) ? null : active === "dashboard" ? (
            <Dashboard refreshKey={refreshKey} />
          ) : active === "alertas" ? (
            <AlertasView onLoaded={setAlertas} />
          ) : active === "documentos" ? (
            <DocumentosView
              itens={itens}
              user={user}
              displayName={displayName}
              onDataChanged={onDataChanged}
            />
          ) : active === "recebimentos" ? (
            // Entrada de material COM conferência: o estoque só muda quando o
            // recebimento é conferido. Ver server/recebimentos.ts.
            <RecebimentosView
              itens={itens}
              user={user}
              displayName={displayName}
              onDataChanged={onDataChanged}
            />
          ) : active === "solicitacoes" ? (
            <SolicitacoesView
              itens={itens}
              user={user}
              displayName={displayName}
              onDataChanged={onDataChanged}
            />
          ) : tab?.key === "solicitar_atualizacao" ? (
              // A aba que existe em todos os sistemas: o pedido vai para o
              // sistema de Desenvolvimento (ver server/solicitar-atualizacao.ts).
              <SolicitarAtualizacaoView usuario={user} />
            ) : tab ? (
            <CrudView
              key={tab.key}
              tab={tab}
              itens={itens}
              onDataChanged={onDataChanged}
            />
          ) : null}
        </section>
      </main>
    </div>
  );

  // O aviso de "pode excluir" desce por contexto: cada tela esconde o seu
  // botão de lixeira sem precisar receber a permissão de prop em prop.
  return <PermissoesProvider value={podeExcluir}>{conteudo}</PermissoesProvider>;
}
